import React, { useCallback, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet";

import { batchService } from "/app/src/services";
import DateTime from "/app/src/components/generic/formatting/dateTime";
import { useTranslation } from "react-i18next";
import { Batch as BatchType } from "/app/src/models";
import { createColumnHelper } from "@tanstack/react-table";
import Table from "/app/src/components/generic/tables/table";
import { useSortUpgrade } from "/app/src/hooks/useSortUpgrade";
import usePaginatedData from "/app/src/hooks/usePaginatedData";
import useQueryParams from "/app/src/hooks/useQueryParams";
import useSetting from "/app/src/components/settings/setting";
import HeroSearch from "/app/src/components/generic/components/heroSearch";
import {
  isColumnSelected,
  toggleExplorerColumn,
  buildExplorerColumns,
} from "/app/src/components/explorer/helpers/columns";

import Breadcrumbs from "/app/src/components/generic/components/breadcrumbs";
import {
  Button,
  Checkbox,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@heroui/react";
import { useAuthState } from "/app/src/contexts/authentication";
/**
 * A React component that displays and manages batches in a table with search, sorting, and pagination.
 *
 * @returns {JSX.Element} The rendered batches component.
 */
export default function Batches() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuthState();
  const columnHelper = createColumnHelper<BatchType>();

  const availableColumns = [
    { id: "priority", label: t("translation:priority") },
    { id: "creationDate", label: t("translation:creation_date") },
    { id: "deadline", label: t("translation:deadline") },
  ];

  const { settingValue, createUpdateSetting } = useSetting({
    name: "explorerColumns",
    userId: user?.id,
  });

  const isColumnChecked = useCallback(
    (id: string) => isColumnSelected(settingValue, "batch", id),
    [settingValue],
  );

  const toggleColumnSelection = useCallback(
    async (id: string) =>
      toggleExplorerColumn(
        settingValue,
        createUpdateSetting,
        "batch",
        id,
        user?.id,
      ),
    [settingValue, createUpdateSetting, user?.id],
  );

  const columns = useMemo(() => {
    /**
     * Creates a column definition based on the given id.
     * @param {string} id - The identifier of the column to create.
     * @returns {Object|null} The column definition object or null if the id is not recognized.
     */
    const makeColumn = (id: string) => {
      switch (id) {
        case "name":
          return columnHelper.accessor("name", {
            id: "name",
            header: () => t("translation:name"),
            cell: (info) => info.getValue(),
          });
        case "priority":
          return columnHelper.accessor("priority", {
            id: "priority",
            header: () => t("translation:priority"),
            cell: (info) => info.getValue(),
          });
        case "creationDate":
          return columnHelper.accessor("creationDate", {
            id: "creationDate",
            header: () => t("translation:creation_date"),
            cell: (info) => <DateTime date={info.getValue()} />,
          });
        case "deadline":
          return columnHelper.accessor("deadline", {
            id: "deadline",
            header: () => t("translation:deadline"),
            cell: (info) => <DateTime date={info.getValue()} />,
          });
        default:
          return null;
      }
    };

    return buildExplorerColumns(settingValue, "batch", makeColumn, "name");
  }, [columnHelper, settingValue, t]);

  const [sort, setSort] = useSortUpgrade([]);
  const [searchParams] = useSearchParams();
  const [searchString, setSearchString] = useState(
    searchParams.get("search") || "",
  );
  const [page, setPage] = useState(
    parseInt(searchParams.get("page") || "1") - 1,
  );
  const [pageSize, setPageSize] = useState(
    parseInt(searchParams.get("pageSize") || "25"),
  );

  /**
   * Navigates to the detailed view of a batch.
   *
   * @param {number} batchId - The ID of the batch.
   */
  const goToBatch = useCallback(
    (batchId: number) => {
      navigate(`/explorer/batches/${batchId}`);
    },
    [navigate],
  );

  const createToggleHandler = useCallback(
    (id: string) => () => {
      toggleColumnSelection(id);
    },
    [toggleColumnSelection],
  );

  const {
    data: batches,
    count: batchesCount,
    isFetching,
  } = usePaginatedData<BatchType>({
    queryKey: ["batches"],
    searchString,
    sort,
    page,
    pageSize,
    service: batchService,
  });

  const { updatePage, updatePageSize } = useQueryParams({
    count: batchesCount,
    isFetching,
    page,
    setPage,
    pageSize,
    setPageSize,
  });

  return (
    <div className="explorer">
      <Helmet>
        <title>{t("translation:batches")} - ItemPath</title>
      </Helmet>
      <Breadcrumbs
        items={[
          { label: "Explorer", to: "/explorer" },
          { label: t("translation:batches") },
        ]}
      />

      <div className="mt-12 mb-4 flex justify-between">
        <div className="flex justify-between gap-2">
          <HeroSearch
            className="w-[300px]"
            setSearchString={setSearchString}
            allowClear
            box={false}
          />
        </div>
        <Popover placement="bottom" radius="md" aria-label="Customize columns">
          <PopoverTrigger>
            <Button size="md" radius="md" color="primary">
              {t("translation:customize_columns")}
            </Button>
          </PopoverTrigger>
          <PopoverContent>
            <div className="w-[300px] p-4">
              <h3 className="text-[16px]">
                {t("translation:available_columns")}
              </h3>
              <div className="mt-2 flex flex-col gap-2">
                {availableColumns.map((column) => (
                  <Checkbox
                    size="sm"
                    key={column.id}
                    defaultSelected={isColumnChecked(column.id)}
                    onChange={createToggleHandler(column.id)}
                  >
                    {column.label}
                  </Checkbox>
                ))}
              </div>
            </div>
          </PopoverContent>
        </Popover>
      </div>

      <Table
        loading={isFetching}
        rows={batches}
        tableColumns={columns}
        length={batchesCount}
        sort={sort}
        setSort={setSort}
        paginationEnabled={{
          currentPage: page,
          pageSize,
          setPage: updatePage,
          setPageSize: updatePageSize,
        }}
        rowClicked={goToBatch}
        emptyText={t("translation:no_batches")}
      />
    </div>
  );
}
